import { eq } from "drizzle-orm";
import {
  list,
  list_group,
  list_group_item,
  type PowerSyncDrizzleDB,
  subtask,
  todo,
} from "@listto/powersync";
import { nowISO } from "../utils/date-helpers";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { generateId } from "@fleet/utils";

/**
 * The `UPDATE ... WHERE id = ?` in this file use `patchRowVerified`: an
 * raw UPDATE against the PowerSync view can match ZERO rows and return
 * "success" while the newly created row is still traveling to the server (see
 * `../powersync/verified-write.ts`). These mutations **can throw**.
 */

export async function createListMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  name: string,
  opts?: {
    color?: string;
    icon?: string;
    groupId?: string;
    themeId?: string | null;
    isBlur?: boolean;
  },
): Promise<string> {
  const id = generateId();
  const now = nowISO();

  await db.transaction(async (trx) => {
    await trx.insert(list).values({
      id,
      name: name.trim(),
      color: opts?.color ?? null,
      icon: opts?.icon ?? null,
      user_id: userId,
      theme_id: opts?.themeId ?? null,
      is_blur: opts?.isBlur === false ? 0 : 1,
      created_at: now,
      updated_at: now,
    });

    if (opts?.groupId) {
      await trx.insert(list_group_item).values({
        id: generateId(),
        list_id: id,
        group_id: opts.groupId,
        user_id: userId,
        added_at: now,
      });
    }
  });

  return id;
}

export async function updateListMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    color: string | null;
    icon: string | null;
    theme_id: string | null;
    is_blur: number;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }

  await patchRowVerified(db, list, list.id, id, finalUpdates, options);
}

export async function createListGroupMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  name: string,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(list_group).values({
    id,
    name: name.trim(),
    user_id: userId,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateListGroupMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{ name: string }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }

  await patchRowVerified(db, list_group, list_group.id, id, finalUpdates, options);
}

export async function assignListToGroupMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  listId: string,
  groupId: string | null,
): Promise<void> {
  await db.transaction(async (trx) => {
    if (groupId) {
      await trx.delete(list_group_item).where(eq(list_group_item.list_id, listId));
      await trx.insert(list_group_item).values({
        id: generateId(),
        list_id: listId,
        group_id: groupId,
        user_id: userId,
        added_at: nowISO(),
      });
    } else {
      await trx.delete(list_group_item).where(eq(list_group_item.list_id, listId));
    }
  });
}

/**
 * Soft-delete of list (trash, ACTIVITY-CORE L2): only marks
 * `deleted_at`. Its tasks/groups remain intact (hidden while list is
 * in trash) and reappear on restore. Permanent purge is
 * `purgeListMutation`.
 */
export async function deleteListOptimisticMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, list, list.id, id, { deleted_at: nowISO() }, options);
}

/** Restores list from trash (its tasks return with it). */
export async function restoreListMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, list, list.id, id, { deleted_at: null }, options);
}

/**
 * PERMANENT purge of list from trash: deletes subtasks, its
 * tasks, its group associations, and the list itself. Irreversible on
 * client; server preserves snapshots in `activity`.
 */
export async function purgeListMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(subtask).where(eq(subtask.list_id, id));
    await trx.delete(todo).where(eq(todo.list_id, id));
    await trx.delete(list_group_item).where(eq(list_group_item.list_id, id));
    await trx.delete(list).where(eq(list.id, id));
  });
}

export async function deleteListGroupOptimisticMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.transaction(async (trx) => {
    await trx.delete(list_group_item).where(eq(list_group_item.group_id, id));
    await trx.delete(list_group).where(eq(list_group.id, id));
  });
}
